import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import api, { BACKEND_URL } from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { 
  Bell, 
  Search, 
  CheckCheck, 
  Calendar, 
  FileText, 
  Paperclip, 
  ChevronRight,
  Eye,
  Plus,
  Edit2,
  Trash2,
  X,
  Check,
  FileUp
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Notifications = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();

  const isAdmin = user?.role === 'Admin';
  
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [readIds, setReadIds] = useState([]);
  
  // Modal / Form States
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [editId, setEditId] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    file: null
  });

  useEffect(() => {
    const savedReadIds = JSON.parse(localStorage.getItem('readNoticeIds') || '[]');
    setReadIds(savedReadIds);
    fetchNotices();
  }, []);

  // Deep-linking support
  useEffect(() => {
    if (notices.length > 0) {
      const searchParams = new URLSearchParams(location.search);
      const noticeIdStr = searchParams.get('id');
      if (noticeIdStr) {
        const id = parseInt(noticeIdStr, 10);
        const notice = notices.find(n => n.id === id);
        if (notice) {
          setSelectedNotice(notice);
          setShowDetailModal(true);
          markAsRead(notice.id);
        }
      }
    }
  }, [location.search, notices]);

  const fetchNotices = async () => {
    try {
      const res = await api.get('/requirements?is_document=false');
      const data = res.data;
      data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setNotices(data);
    } catch (err) {
      console.error('Error fetching requirements:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = (id) => {
    setReadIds(prev => {
      if (prev.includes(id)) return prev;
      const updated = [...prev, id];
      localStorage.setItem('readNoticeIds', JSON.stringify(updated));
      window.dispatchEvent(new Event('noticeReadUpdate'));
      return updated;
    });
  };

  const handleMarkAllRead = () => {
    const allIds = notices.map(n => n.id);
    setReadIds(allIds);
    localStorage.setItem('readNoticeIds', JSON.stringify(allIds));
    window.dispatchEvent(new Event('noticeReadUpdate'));
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this notice?')) return;
    try {
      await api.delete(`/requirements/${id}`);
      fetchNotices();
      // If deleted notice is active in details modal, close it
      if (selectedNotice?.id === id) {
        setShowDetailModal(false);
      }
    } catch (err) {
      console.error('Error deleting notice:', err);
      alert('Error deleting notice');
    }
  };

  const handleEditClick = (notice, e) => {
    e.stopPropagation();
    setEditId(notice.id);
    setFormData({
      title: notice.title,
      description: notice.description || '',
      file: null
    });
    setShowFormModal(true);
  };

  const handleAddClick = () => {
    setEditId(null);
    setFormData({
      title: '',
      description: '',
      file: null
    });
    setShowFormModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append('title', formData.title);
    data.append('description', formData.description);
    data.append('is_document', 'false');
    if (formData.file) data.append('file', formData.file);

    try {
      if (editId) {
        await api.put(`/requirements/${editId}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/requirements', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setShowFormModal(false);
      fetchNotices();
    } catch (err) {
      console.error('Error saving notice:', err);
      alert('Error saving notice');
    }
  };

  const handleRowClick = (notice) => {
    setSelectedNotice(notice);
    setShowDetailModal(true);
    markAsRead(notice.id);
  };

  const filteredNotices = notices.filter(notice => 
    notice.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (notice.description && notice.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
          <p className="text-gray-500 font-bold animate-pulse">Loading Notice Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500 max-w-[1400px] mx-auto pb-10">
      {/* Header section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <Bell className="text-indigo-600 animate-bounce animate-duration-1000" size={28} />
            Announcements & Notices
          </h2>
          <p className="text-gray-400 text-sm font-medium mt-1">Manage and view official announcements and system notices.</p>
        </div>
        
        <div className="flex items-center gap-3 self-start sm:self-auto">
          {notices.length > 0 && (
            <button 
              onClick={handleMarkAllRead}
              className="flex items-center justify-center gap-2 px-5 py-3 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-2xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 border border-gray-200/50"
            >
              <CheckCheck size={16} /> Mark all read
            </button>
          )}
          {isAdmin && (
            <button 
              onClick={handleAddClick}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-indigo-100"
            >
              <Plus size={16} /> Add Notice
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="relative max-w-md">
        <input 
          type="text" 
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search by title or message..."
          className="w-full pl-12 pr-6 py-4 rounded-2xl bg-white border border-gray-100 shadow-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-gray-800 placeholder:text-gray-400 transition-all"
        />
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
      </div>

      {/* Tabular List Dashboard */}
      <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl shadow-indigo-900/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="px-6 py-5 text-xs font-black text-gray-400 uppercase tracking-widest w-24">Status</th>
                <th className="px-6 py-5 text-xs font-black text-gray-400 uppercase tracking-widest w-80">Title</th>
                <th className="px-6 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">Message</th>
                <th className="px-6 py-5 text-xs font-black text-gray-400 uppercase tracking-widest w-36">Date</th>
                <th className="px-6 py-5 text-xs font-black text-gray-400 uppercase tracking-widest w-28 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredNotices.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-20 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center text-gray-300 mb-4">
                        <Bell size={28} />
                      </div>
                      <p className="text-gray-400 font-black uppercase tracking-widest text-xs">No announcements found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredNotices.map((notice) => {
                  const isRead = readIds.includes(notice.id);
                  return (
                    <tr 
                      key={notice.id} 
                      onClick={() => handleRowClick(notice)}
                      className="hover:bg-gray-50/60 transition-colors cursor-pointer group"
                    >
                      {/* Status */}
                      <td className="px-6 py-5 whitespace-nowrap">
                        {isRead ? (
                          <span className="px-3 py-1 bg-gray-100 text-gray-500 rounded-full text-[10px] font-black uppercase tracking-wider border border-gray-200/20">
                            Read
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-[10px] font-black uppercase tracking-wider border border-indigo-100/50 animate-pulse">
                            Unread
                          </span>
                        )}
                      </td>
                      {/* Title */}
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-2">
                          <div className={`text-sm font-bold text-gray-900 leading-snug break-words ${!isRead ? 'font-black text-gray-950' : 'text-gray-700'}`}>
                            {notice.title}
                          </div>
                          {notice.file_path && (
                            <Paperclip size={14} className="text-emerald-500 shrink-0" title="Has attachment" />
                          )}
                        </div>
                      </td>
                      {/* Message */}
                      <td className="px-6 py-5">
                        <div className="text-sm font-medium text-gray-500 line-clamp-2 leading-relaxed max-w-xl">
                          {notice.description || 'No description provided.'}
                        </div>
                      </td>
                      {/* Date */}
                      <td className="px-6 py-5 whitespace-nowrap text-sm font-bold text-gray-400">
                        {formatDate(notice.created_at)}
                      </td>
                      {/* Actions */}
                      <td className="px-6 py-5 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-2">
                          <button 
                            onClick={() => handleRowClick(notice)}
                            className="p-2 bg-gray-50 hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 rounded-xl transition-colors border border-gray-100 hover:border-indigo-100 shadow-sm"
                            title="View Notice Details"
                          >
                            <Eye size={15} />
                          </button>
                          {isAdmin && (
                            <>
                              <button 
                                onClick={(e) => handleEditClick(notice, e)}
                                className="p-2 bg-gray-50 hover:bg-amber-50 text-gray-400 hover:text-amber-600 rounded-xl transition-colors border border-gray-100 hover:border-amber-100 shadow-sm"
                                title="Edit Notice"
                              >
                                <Edit2 size={15} />
                              </button>
                              <button 
                                onClick={(e) => handleDelete(notice.id, e)}
                                className="p-2 bg-gray-50 hover:bg-rose-50 text-gray-400 hover:text-rose-600 rounded-xl transition-colors border border-gray-100 hover:border-rose-100 shadow-sm"
                                title="Delete Notice"
                              >
                                <Trash2 size={15} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Admin Add/Edit Form Modal */}
      <AnimatePresence>
        {showFormModal && isAdmin && (
          <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-[2.5rem] border border-gray-100 shadow-2xl max-w-xl w-full p-8 md:p-10 relative overflow-hidden"
            >
              <button 
                onClick={() => setShowFormModal(false)}
                className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center bg-gray-50 hover:bg-gray-100 rounded-full text-gray-400 hover:text-rose-600 shadow-sm border border-gray-100/50 z-10 transition-all"
              >
                <X size={20} />
              </button>

              <h3 className="text-xl font-black text-gray-900 mb-8 flex items-center gap-3">
                <div className="w-2.5 h-6 bg-indigo-600 rounded-full" />
                {editId ? 'Modify Announcement' : 'Post New Announcement'}
              </h3>

              <form onSubmit={handleSave} className="space-y-6">
                <div>
                  <label className="block text-[10px] font-black text-indigo-900/50 uppercase tracking-widest mb-2 ml-1">Title</label>
                  <input 
                    type="text" 
                    value={formData.title} 
                    onChange={e => setFormData({...formData, title: e.target.value})} 
                    className="w-full px-6 py-4 rounded-2xl bg-gray-50 focus:bg-white border border-transparent focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 font-bold text-gray-800 placeholder:text-gray-300 transition-all"
                    required 
                    placeholder="e.g. JLCT Exam Date Announced"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-indigo-900/50 uppercase tracking-widest mb-2 ml-1">Message Description</label>
                  <textarea 
                    value={formData.description} 
                    onChange={e => setFormData({...formData, description: e.target.value})} 
                    className="w-full px-6 py-4 rounded-2xl bg-gray-50 focus:bg-white border border-transparent focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 font-medium text-gray-750 placeholder:text-gray-300 min-h-[150px] transition-all"
                    placeholder="Enter details..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-indigo-900/50 uppercase tracking-widest mb-2 ml-1">Attachment (Optional)</label>
                  <div className="w-full px-6 py-4 rounded-2xl bg-gray-50 flex items-center border border-transparent focus-within:border-indigo-500 transition-all">
                    <FileUp className="text-gray-400 mr-3" size={20} />
                    <input 
                      type="file" 
                      onChange={e => setFormData({...formData, file: e.target.files[0]})} 
                      className="w-full text-sm font-semibold text-gray-600 file:mr-4 file:py-1.5 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-black file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 transition-all cursor-pointer"
                      accept=".pdf,.jpg,.jpeg,.png"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <button 
                    type="submit" 
                    className="flex items-center gap-2 px-10 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-sm shadow-xl shadow-indigo-100 transition-all active:scale-95"
                  >
                    <Check size={20} /> {editId ? 'Save Changes' : 'Post Announcement'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Notice Detail View Modal */}
      <AnimatePresence>
        {showDetailModal && selectedNotice && (
          <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-[2.5rem] border border-gray-100 shadow-2xl max-w-2xl w-full overflow-hidden relative flex flex-col max-h-[90vh]"
            >
              <button 
                onClick={() => setShowDetailModal(false)}
                className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center bg-gray-50 hover:bg-gray-100 rounded-full text-gray-400 hover:text-rose-600 shadow-sm border border-gray-100/50 z-10 transition-all"
              >
                <X size={20} />
              </button>

              <div className="p-8 md:p-10 border-b border-gray-50 bg-indigo-50/20 relative">
                <h3 className="text-2xl md:text-3xl font-black text-gray-955 tracking-tight leading-tight mb-4 pr-10">
                  {selectedNotice.title}
                </h3>

                <div className="flex items-center gap-1.5 text-xs font-bold text-gray-400">
                  <Calendar size={14} className="text-indigo-500" />
                  {formatDate(selectedNotice.created_at)}
                </div>
              </div>

              <div className="p-8 md:p-10 overflow-y-auto flex-1 space-y-8">
                <div className="prose prose-indigo max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap font-medium text-base">
                    {selectedNotice.description}
                  </p>
                </div>

                {/* Attachments Section */}
                {selectedNotice.file_path && (
                  <div className="pt-8 border-t border-gray-100">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Attachments</h4>
                    <a 
                      href={selectedNotice.file_path.startsWith('http') ? selectedNotice.file_path : `${BACKEND_URL}${selectedNotice.file_path}`} 
                      target="_blank" 
                      rel="noreferrer"
                      className="flex items-center gap-4 p-4 rounded-2xl border border-indigo-50 hover:border-indigo-200 bg-indigo-50/10 hover:bg-indigo-50/30 transition-all max-w-md group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-white border border-gray-100 shadow-sm flex items-center justify-center text-indigo-600 group-hover:scale-105 transition-transform">
                        <FileText size={24} />
                      </div>
                      <div className="flex-1 overflow-hidden">
                        <p className="text-sm font-bold text-gray-900 truncate">Document Attachment</p>
                        <p className="text-[10px] text-gray-400 font-bold uppercase mt-0.5 tracking-wider">Click to view/download</p>
                      </div>
                      <ChevronRight size={18} className="text-gray-300 group-hover:text-indigo-600 transition-colors" />
                    </a>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Notifications;
